import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';

import { ICON_STROKE_WIDTH } from '../../constants';
import patientAccessLinkValidityService, {
  type PatientAccessLinkValidity,
} from '../../services/patientAccessLinkValidityService';

/** Max validity window: 1 year in hours (must match backend). */
export const MAX_PATIENT_ACCESS_LINK_HOURS = 24 * 365;

interface PatientLinkTimeRecord {
  id: number;
  hours: number;
  updatedAt: string;
}

interface PatientLinkTimeSectionProps {
  facilityId: number;
}

/** Digits only; empty string allowed while typing. */
function sanitizeHoursInput(value: string): string {
  return value.replace(/\D/g, '');
}

function parsePositiveIntegerHours(value: string): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const hours = Number(value);
  if (!Number.isInteger(hours) || hours < 1) {
    return null;
  }
  if (hours > MAX_PATIENT_ACCESS_LINK_HOURS) {
    return null;
  }
  return hours;
}

function mapApiRecord(row: PatientAccessLinkValidity): PatientLinkTimeRecord {
  return {
    id: row.id,
    hours: row.hours,
    updatedAt: row.updatedAt || row.createdAt || new Date().toISOString(),
  };
}

function formatHoursLabel(hours: number): string {
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

function PatientLinkTimeTableBody({
  loading,
  record,
  saving,
  onEdit,
  onDeleteClick,
}: Readonly<{
  loading: boolean;
  record: PatientLinkTimeRecord | null;
  saving: boolean;
  onEdit: () => void;
  onDeleteClick: () => void;
}>) {
  if (loading) {
    return (
      <tr>
        <td
          colSpan={4}
          className="esc-table-empty"
        >
          Loading…
        </td>
      </tr>
    );
  }
  if (!record) {
    return (
      <tr>
        <td
          colSpan={4}
          className="esc-table-empty"
        >
          No patient access link validity configured. Add a record to set validity in hours.
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="esc-table-index">1</td>
      <td className="esc-table-name">{formatHoursLabel(record.hours)}</td>
      <td>
        {new Date(record.updatedAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </td>
      <td className="esc-table-actions-col">
        <div className="esc-table-actions">
          <button
            type="button"
            className="esc-icon-btn"
            aria-label="Edit patient access link validity"
            onClick={onEdit}
            disabled={saving}
          >
            <Pencil
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
          <button
            type="button"
            className="esc-icon-btn esc-icon-btn--danger"
            aria-label="Delete patient access link validity"
            onClick={onDeleteClick}
            disabled={saving}
          >
            <Trash2
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
        </div>
      </td>
    </tr>
  );
}

function PatientLinkTimeEditDialog({
  open,
  isEditing,
  hoursInput,
  fieldError,
  saving,
  onClose,
  onHoursChange,
  onHoursKeyDown,
  onSave,
}: Readonly<{
  open: boolean;
  isEditing: boolean;
  hoursInput: string;
  fieldError: string | null;
  saving: boolean;
  onClose: () => void;
  onHoursChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHoursKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onSave: () => void;
}>) {
  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => !nextOpen && onClose()}
    >
      <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {isEditing ? 'Edit patient access link validity' : 'Add patient access link validity'}
          </DialogTitle>
        </DialogHeader>

        <div className="esc-activate-dialog-body">
          <label
            className="esc-field"
            htmlFor="patient-link-hours"
          >
            <span className="esc-field-label">Hours</span>
            <input
              id="patient-link-hours"
              className={['esc-input', fieldError ? 'esc-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              placeholder="e.g. 24"
              value={hoursInput}
              onChange={onHoursChange}
              onKeyDown={onHoursKeyDown}
              aria-invalid={Boolean(fieldError)}
              aria-describedby={fieldError ? 'patient-link-hours-error' : 'patient-link-hours-help'}
              disabled={saving}
            />
            <span
              id="patient-link-hours-help"
              className="esc-field-helper"
            >
              Whole numbers from 1 to {MAX_PATIENT_ACCESS_LINK_HOURS} (1 year). Decimals, negatives,
              and letters are not allowed.
            </span>
            {fieldError ? (
              <span
                id="patient-link-hours-error"
                className="esc-field-error"
                role="alert"
              >
                {fieldError}
              </span>
            ) : null}
          </label>
        </div>

        <div className="esc-dialog-actions">
          <button
            type="button"
            className="esc-btn esc-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="esc-btn esc-btn--primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PatientLinkTimeDeleteDialog({
  open,
  record,
  saving,
  onClose,
  onConfirm,
}: Readonly<{
  open: boolean;
  record: PatientLinkTimeRecord | null;
  saving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}>) {
  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => !nextOpen && !saving && onClose()}
    >
      <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Delete patient access link validity
          </DialogTitle>
        </DialogHeader>

        <div className="esc-activate-dialog-body">
          <p>
            Are you sure you want to delete the patient access link validity of{' '}
            <strong>{record ? formatHoursLabel(record.hours) : ''}</strong>? You can create a new
            record afterward.
          </p>
        </div>

        <div className="esc-dialog-actions">
          <button
            type="button"
            className="esc-btn esc-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="esc-btn esc-btn--primary study-assignment-delete-confirm-btn"
            onClick={onConfirm}
            disabled={saving}
          >
            {saving ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PatientLinkTimeSection(props: Readonly<PatientLinkTimeSectionProps>) {
  return <PatientLinkTimeSectionInner {...props} />;
}

function PatientLinkTimeSectionInner({ facilityId }: Readonly<PatientLinkTimeSectionProps>) {
  const [record, setRecord] = useState<PatientLinkTimeRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hoursInput, setHoursInput] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const loadRecord = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await patientAccessLinkValidityService.list(facilityId);
    if (!result.success) {
      setRecord(null);
      setLoadError(result.error || 'Failed to load patient access link validity');
      setLoading(false);
      return;
    }
    const first = result.data?.[0];
    setRecord(first ? mapApiRecord(first) : null);
    setLoading(false);
  }, [facilityId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const openCreateModal = () => {
    setIsEditing(false);
    setHoursInput('');
    setFieldError(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    if (!record) {
      return;
    }
    setIsEditing(true);
    setHoursInput(String(record.hours));
    setFieldError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }
    setModalOpen(false);
    setFieldError(null);
  };

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeHoursInput(event.target.value);
    setHoursInput(next);
    if (fieldError) {
      setFieldError(null);
    }
  };

  const handleHoursKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleSave = useCallback(async () => {
    const hours = parsePositiveIntegerHours(hoursInput);
    if (hours == null) {
      setFieldError(
        `Enter a whole number of hours between 1 and ${MAX_PATIENT_ACCESS_LINK_HOURS} (1 year).`
      );
      return;
    }

    setSaving(true);
    setFieldError(null);

    const result =
      isEditing && record
        ? await patientAccessLinkValidityService.update(record.id, { facilityId, hours })
        : await patientAccessLinkValidityService.create({ facilityId, hours });

    setSaving(false);

    if (!result.success || !result.data) {
      setFieldError(result.error || 'Failed to save patient access link validity');
      return;
    }

    setRecord(mapApiRecord(result.data));
    setModalOpen(false);
  }, [facilityId, hoursInput, isEditing, record]);

  const handleDelete = async () => {
    if (!record) {
      return;
    }
    setSaving(true);
    const result = await patientAccessLinkValidityService.remove(record.id, facilityId);
    setSaving(false);
    if (!result.success) {
      setLoadError(result.error || 'Failed to delete patient access link validity');
      setDeleteConfirmOpen(false);
      return;
    }
    setRecord(null);
    setDeleteConfirmOpen(false);
  };

  return (
    <section className="esc-step-card">
      <header className="esc-step-header">
        <div className="esc-step-title-row">
          <h2 className="esc-step-title">Patient access link validity</h2>
          <span className="esc-count-badge">{record ? 1 : 0}</span>
        </div>
        <p className="esc-step-subtitle">
          Set how many hours a patient access link remains valid after the latest SMS or WhatsApp
          dispatch. Only one configuration is allowed — edit or delete the existing record to change
          it.
        </p>
      </header>

      {loadError ? (
        <p
          className="esc-field-error"
          role="alert"
        >
          {loadError}
        </p>
      ) : null}

      <div className="esc-toolbar esc-toolbar--contacts">
        <div className="esc-toolbar-actions">
          <button
            type="button"
            className="esc-btn esc-btn--primary esc-btn--inline"
            onClick={openCreateModal}
            disabled={Boolean(record) || loading || saving}
            title={record ? 'Delete the existing record before creating a new one' : undefined}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add patient access link validity
          </button>
        </div>
      </div>

      <div className="esc-table-wrap">
        <table className="esc-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Hours</th>
              <th scope="col">Last updated</th>
              <th
                className="esc-table-actions-col"
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>
            <PatientLinkTimeTableBody
              loading={loading}
              record={record}
              saving={saving}
              onEdit={openEditModal}
              onDeleteClick={() => setDeleteConfirmOpen(true)}
            />
          </tbody>
        </table>
      </div>

      <PatientLinkTimeEditDialog
        open={modalOpen}
        isEditing={isEditing}
        hoursInput={hoursInput}
        fieldError={fieldError}
        saving={saving}
        onClose={closeModal}
        onHoursChange={handleHoursChange}
        onHoursKeyDown={handleHoursKeyDown}
        onSave={handleSave}
      />

      <PatientLinkTimeDeleteDialog
        open={deleteConfirmOpen}
        record={record}
        saving={saving}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
